import { colors } from '@configs/styles/config'
import styled from '@emotion/styled'
import EarningIcon from '@public/static/icons/referrals-landing/earningIcon'
import MoneySendIcon from '@public/static/icons/referrals-landing/moneySendIcon'
import WalletIcon from '@public/static/icons/referrals-landing/walletIcon'
import { useTranslation } from 'react-i18next'

type Props = {}

const HeroSection = ({}: Props) => {
  const { t } = useTranslation('referrals')

  const highlights = [
    {
      title: t('highlights.unlimited_title'),
      desc: t('highlights.unlimited_desc'),
      icon: (
        <EarningIcon width={'100%'} height={'100%'} color={colors.uguWhite} />
      ),
      bgColor: colors.uguPurple,
      textColor: colors.uguWhite,
      borderColor: '#3C4063',
    },
    {
      title: t('highlights.powerful_title'),
      desc: t('highlights.powerful_desc'),
      icon: (
        <MoneySendIcon width={'100%'} height={'100%'} color={colors.uguWhite} />
      ),

      bgColor: colors.uguBrightPurple,
      textColor: colors.uguWhite,
      borderColor: '#8593FF',
    },
    {
      title: t('highlights.global_title'),
      desc: t('highlights.global_desc'),
      icon: (
        <WalletIcon width={'100%'} height={'100%'} color={colors.uguPurple} />
      ),
      bgColor: '#E1E5FF',
      borderColor: '#c6ccf4',
    },
  ]

  return (
    <Container>
      <Wrapper>
        <Title>{t('invite_and_earn_title')}</Title>
        <Description>{t('invite_and_earn_description')}</Description>
        <div className="flex gap-4 items-center md:mt-10 mt-8 justify-center flex-wrap">
          {highlights.map((h) => (
            <Button
              key={h.title}
              bgColor={h.bgColor}
              textColor={h.textColor}
              className="shadow-sm"
            >
              <IconWrapper borderColor={h.borderColor}>{h.icon}</IconWrapper>
              <div className="flex gap-1">
                <ButtonHighlight>{h.title}</ButtonHighlight>
                <ButtonSpan>{h.desc}</ButtonSpan>
              </div>
            </Button>
          ))}
        </div>
      </Wrapper>
    </Container>
  )
}

export default HeroSection

const Container = styled.div`
  height: 630px;
  border-radius: 1rem;
  background: #f7f7f7;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 640px) {
    height: 700px;
  }
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 944px;
  padding: 0rem 1rem;
  margin-top: -3rem;
  @media (max-width: 800px) {
    gap: 1.6rem;
    margin-top: -1rem;
  }
  @media (max-width: 640px) {
    gap: 1.2rem;
    margin-top: 0rem;
    gap: 1.6rem;
    margin-top: -1rem;
  }
`
const Title = styled.div`
  font-family: var(--font-var(--font-roboto));
  font-size: 68px;
  font-weight: 700;
  line-height: 78px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  @media (max-width: 800px) {
    line-height: 62px;
    font-size: 54px;
  }
  @media (max-width: 640px) {
    line-height: 52px;
    font-size: 46px;
  }
  color: #1a1e3d;
`
const Description = styled.div`
  font-family: var(--font-var(--font-roboto));
  font-size: 22px;
  font-weight: 400;
  line-height: 33px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #53566e;
  @media (max-width: 800px) {
    line-height: 26px;
    font-size: 20px;
  }
  @media (max-width: 640px) {
    line-height: 22px;
    font-size: 18px;
  }
`
const Button = styled.div`
  background-color: ${(props) => (props.bgColor ? props.bgColor : 'white')};
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 1.2rem;
  border-radius: 1rem;
  user-select: none;
  color: ${(props) => (props.textColor ? props.textColor : colors.uguPurple)};
`
const ButtonHighlight = styled.div`
  font-family: var(--font-roboto);
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  @media (max-width: 700px) {
    font-size: 18px;
    line-height: 22px;
  }
  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 20px;
  }
`
const ButtonSpan = styled.div`
  font-family: var(--font-roboto);
  font-weight: 400;
  font-size: 20px;
  line-height: 26px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  @media (max-width: 700px) {
    font-size: 18px;
    line-height: 22px;
  }
  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 20px;
  }
`
const IconWrapper = styled.div`
  padding: 11px;
  border-radius: 3rem;
  width: 48px;
  height: 48px;
  @media (max-width: 700px) {
    padding: 9px;
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  }
  @media (max-width: 480px) {
    padding: 7px;
    width: 42px;
    height: 42px;
    min-width: 42px;
    min-height: 42px;
  }
  border: 1px solid ${(props) => (props.borderColor ? props.borderColor : '')};
`
